import { cn } from "@client/utils/cn";
import { TableHead } from "@sfc/ui";
import type { ComponentProps } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const sortableHeaderClass =
  "flex pointer-coarse:min-h-11 pointer-coarse:min-w-11 cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 -mx-1 -my-0.5 text-label-md font-medium text-text-secondary transition-colors hover:text-accent outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

export function SortableTableHead<TSort extends string>({
  label,
  sortKey,
  currentSort,
  sortDirection,
  onSort,
  className,
  align = "left",
  ...layoutProps
}: {
  label: string;
  sortKey: TSort;
  currentSort: TSort;
  sortDirection: "asc" | "desc";
  onSort: (key: TSort) => void;
  className?: string;
  align?: "left" | "center" | "right";
} & Pick<ComponentProps<typeof TableHead>, "wide" | "narrow" | "numeric">) {
  const isActive = currentSort === sortKey;
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";
  const buttonJustify =
    align === "right"
      ? "justify-end w-full"
      : align === "center"
        ? "justify-center w-full"
        : "";
  return (
    <TableHead
      {...layoutProps}
      aria-sort={
        isActive
          ? sortDirection === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      className={cn("text-text-secondary", alignClass, className)}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          sortableHeaderClass,
          buttonJustify,
          isActive && "text-accent",
        )}
      >
        {label}
        {isActive ? (
          sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        )}
      </button>
    </TableHead>
  );
}
